import {
  Close as CloseIcon,
  EditOutlined as EditIcon,
  Inventory2Outlined as InventoryIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import { tokens } from "../../../../theme";
import { IBrandModel } from "../../../../types/equipment";
import AddSetDialog from "../../../gear-management/components/AddSetDialog";
import { PrintableGearSet } from "./PrintableGear";

const TEXT_MUTED = "#94A3B8";

interface GearSetItem {
  id: number;
  brand_model: number | null;
  brand_model_name?: string;
  quantity?: number;
  notes?: string;
}

interface GearSet {
  id: number;
  name: string;
  description: string;
  items_count: number;
  status: string;
  items?: GearSetItem[];
}

interface EquipmentAllocation {
  id: number;
  gearset_id?: number | null;
  equipment: number | null;
  stream_id?: number;
  stream?: { id: number };
  gearset?: GearSet;
  allocation_date: string;
  allocation_description?: string;
  return_status?: string;
  allocation_type?: "planned" | "confirmed";
}

interface EquipmentLookupEntry {
  id: number;
  asset_tag: string;
  serial_number: string;
  status: string;
  brand_model_id: number | null;
  brand_model_name: string;
  category_name: string;
}

interface GearPageProps {
  onGearSetAllocated?: (gearSet: PrintableGearSet | null) => void;
}

const GearPage: React.FC<GearPageProps> = ({ onGearSetAllocated }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams<{ id: string }>();

  const [gearSets, setGearSets] = useState<GearSet[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [allocations, setAllocations] = useState<EquipmentAllocation[]>([]);
  const [brandModelList, setBrandModelList] = useState<IBrandModel[]>([]);
  const [equipmentMap, setEquipmentMap] = useState<
    Record<number, EquipmentLookupEntry>
  >({});

  const [editSetOpen, setEditSetOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picks, setPicks] = useState<Record<number, number[]>>({});

  const [loadingGearSets, setLoadingGearSets] = useState(false);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const streamId = Number(id);

  const selectedSet = gearSets.find((set) => set.id === selectedSetId);

  const getBrandModel = useCallback(
    (brandModelId: number | null) =>
      brandModelList.find((bm) => Number(bm.id) === brandModelId),
    [brandModelList],
  );

  const fetchGearSets = useCallback(async () => {
    try {
      setLoadingGearSets(true);
      setError(null);

      const response = await apiService.get<{ results: GearSet[] }>(
        "/api/equipments/gearsets/",
      );

      const results = response.data.results || [];
      setGearSets(results);
      setSelectedSetId((prev) => prev ?? results[0]?.id ?? null);
    } catch (err) {
      console.error(err);
      setError("Failed to load gear sets.");
    } finally {
      setLoadingGearSets(false);
    }
  }, []);

  const fetchBrandModels = useCallback(async () => {
    try {
      const res = await apiService.get<{ results: IBrandModel[] }>(
        "/api/equipments/brand-model/",
      );
      setBrandModelList(res.data.results);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchEquipmentMap = useCallback(async () => {
    try {
      const res = await apiService.get<{
        results: Array<{
          id: number;
          asset_tag?: string;
          serial_number: string;
          status: string;
          brand_model?: IBrandModel;
        }>;
      }>("/api/equipments/equipment/");

      const map: Record<number, EquipmentLookupEntry> = {};
      (res.data.results || []).forEach((eq) => {
        const bm = eq.brand_model;
        const category =
          typeof bm?.category === "object" ? bm?.category?.name : bm?.category;
        map[eq.id] = {
          id: eq.id,
          asset_tag: eq.asset_tag || "",
          serial_number: eq.serial_number,
          status: eq.status,
          brand_model_id: bm ? Number(bm.id) : null,
          brand_model_name: bm
            ? `${bm.brand_name || ""} ${bm.model || ""}`.trim()
            : "Unknown",
          category_name: category || "—",
        };
      });
      setEquipmentMap(map);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchAllocations = useCallback(async () => {
    if (!streamId) return;

    try {
      setLoadingAllocations(true);

      const response = await apiService.get<{ results: EquipmentAllocation[] }>(
        `/api/equipments/equipment-allocations/?stream_id=${streamId}`,
      );

      setAllocations(response.data.results || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load allocated gear.");
    } finally {
      setLoadingAllocations(false);
    }
  }, [streamId]);

  const getAllocationStreamId = (allocation: EquipmentAllocation) =>
    allocation.stream_id ?? allocation.stream?.id;

  const getAllocationGearSetId = (allocation: EquipmentAllocation) =>
    allocation.gearset_id ?? allocation.gearset?.id;

  const getAllocationsForGearSet = useCallback(
    (gearSetId: number) =>
      allocations.filter(
        (allocation) =>
          getAllocationGearSetId(allocation) === gearSetId &&
          getAllocationStreamId(allocation) === streamId,
      ),
    [allocations, streamId],
  );

  const isGearSetAllocatedToCurrentStream = useCallback(
    (gearSetId: number) => getAllocationsForGearSet(gearSetId).length > 0,
    [getAllocationsForGearSet],
  );

  const currentStreamAllocations = selectedSetId
    ? getAllocationsForGearSet(selectedSetId)
    : [];

  const isGearSetAlreadyAllocated = currentStreamAllocations.length > 0;
  const isConfirmed = currentStreamAllocations.some(
    (a) => a.allocation_type === "confirmed",
  );

  useEffect(() => {
    fetchGearSets();
    fetchBrandModels();
    fetchEquipmentMap();
  }, [fetchGearSets, fetchBrandModels, fetchEquipmentMap]);

  useEffect(() => {
    fetchAllocations();
  }, [fetchAllocations]);

  // Builds the table/print item list. Confirmed sets show real tagged units;
  // planned sets show the aggregated kit spec (unchanged behavior).
  const buildDisplayItems = useCallback(
    (gearSetId: number) => {
      const set = gearSets.find((s) => s.id === gearSetId);
      if (!set) return [];

      const confirmedRows = getAllocationsForGearSet(gearSetId).filter(
        (a) => a.allocation_type === "confirmed" && a.equipment,
      );

      if (confirmedRows.length > 0) {
        return confirmedRows.map((row) => {
          const unit = equipmentMap[row.equipment as number];
          const label = unit
            ? `${unit.brand_model_name} (${unit.asset_tag || unit.serial_number})`
            : `Equipment #${row.equipment}`;
          return {
            id: row.id,
            name: label,
            category: unit?.category_name || "—",
            quantity: 1,
          };
        });
      }

      return (set.items || []).map((item) => {
        const bm = getBrandModel(item.brand_model);
        return {
          id: item.id,
          name: item.brand_model_name || bm?.model || "—",
          category:
            (typeof bm?.category === "object"
              ? bm?.category?.name
              : bm?.category) || "—",
          quantity: item.quantity || 1,
        };
      });
    },
    [gearSets, getAllocationsForGearSet, equipmentMap, getBrandModel],
  );

  useEffect(() => {
    if (!onGearSetAllocated) return;

    const allocated = gearSets.find((set) =>
      isGearSetAllocatedToCurrentStream(set.id),
    );

    if (!allocated) {
      onGearSetAllocated(null);
      return;
    }

    onGearSetAllocated({
      ...allocated,
      items: buildDisplayItems(allocated.id),
    });
  }, [
    allocations,
    gearSets,
    isGearSetAllocatedToCurrentStream,
    buildDisplayItems,
    onGearSetAllocated,
  ]);

  const handleAllocateGearSet = async () => {
    if (!streamId) {
      toast.error("Stream id is missing.");
      return;
    }
    if (!selectedSetId) {
      toast.error("Please select a gear set.");
      return;
    }

    try {
      setSaving(true);

      for (const row of currentStreamAllocations) {
        await apiService.delete(
          `/api/equipments/equipment-allocations/${row.id}/`,
        );
      }

      await apiService.post("/api/equipments/equipment-allocations/", {
        stream_id: streamId,
        gearset_id: selectedSetId,
        allocation_date: new Date().toISOString(),
        allocation_description: "Allocated from callsheet",
        return_status: "allocated",
        allocation_type: "planned",
      });

      toast.success("Gear set added to callsheet.");
      await fetchAllocations();
    } catch {
      toast.error("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmAuto = async () => {
    if (!streamId || !selectedSetId || currentStreamAllocations.length === 0)
      return;

    try {
      setSaving(true);

      for (const row of currentStreamAllocations) {
        await apiService.delete(
          `/api/equipments/equipment-allocations/${row.id}/`,
        );
      }

      await apiService.post("/api/equipments/equipment-allocations/", {
        stream_id: streamId,
        gearset_id: selectedSetId,
        allocation_date: new Date().toISOString(),
        allocation_description: "Confirmed from callsheet",
        return_status: "allocated",
        allocation_type: "confirmed",
      });

      toast.success("Gear confirmed and reserved.");
      await fetchAllocations();
      await fetchEquipmentMap();
    } catch (err: unknown) {
      const data = (
        err as {
          response?: {
            data?: { gearset?: string | string[]; detail?: string };
          };
        }
      )?.response?.data;
      const msg = data?.gearset || data?.detail;

      try {
        await apiService.post("/api/equipments/equipment-allocations/", {
          stream_id: streamId,
          gearset_id: selectedSetId,
          allocation_date: new Date().toISOString(),
          allocation_description: "Allocated from callsheet",
          return_status: "allocated",
          allocation_type: "planned",
        });
      } catch (e) {
        console.error(e);
      }

      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : msg || "Failed to confirm gear — not enough stock available.",
      );
      await fetchAllocations();
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmManual = async () => {
    if (!streamId || !selectedSetId || !selectedSet) return;

    const requirements = selectedSet.items || [];
    const missing = requirements.find(
      (req) =>
        (picks[req.brand_model ?? -1]?.length || 0) < (req.quantity || 1),
    );
    if (missing) {
      toast.error(
        `Pick ${missing.quantity || 1} unit(s) for ${missing.brand_model_name}.`,
      );
      return;
    }

    try {
      setSaving(true);

      for (const row of currentStreamAllocations) {
        await apiService.delete(
          `/api/equipments/equipment-allocations/${row.id}/`,
        );
      }

      const allPicks = Object.values(picks).flat();
      for (const equipmentId of allPicks) {
        await apiService.post("/api/equipments/equipment-allocations/", {
          equipment: equipmentId,
          gearset_id: selectedSetId,
          stream_id: streamId,
          allocation_type: "confirmed",
          allocation_date: new Date().toISOString(),
          allocation_description: "Manually confirmed from callsheet",
          return_status: "allocated",
        });
      }

      toast.success("Gear confirmed with manual selections.");
      setPickerOpen(false);
      setPicks({});
      await fetchAllocations();
      await fetchEquipmentMap();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { equipment?: string } } })
        ?.response?.data;
      toast.error(data?.equipment || "Failed to confirm selected gear.");
      await fetchAllocations();
    } finally {
      setSaving(false);
    }
  };

  const handleUnallocateGearSet = async () => {
    if (!selectedSetId) {
      toast.error("Please select a gear set.");
      return;
    }

    const rows = getAllocationsForGearSet(selectedSetId);
    if (rows.length === 0) {
      toast.error("No allocation found for this gear set.");
      return;
    }

    try {
      setSaving(true);

      for (const row of rows) {
        await apiService.delete(
          `/api/equipments/equipment-allocations/${row.id}/`,
        );
      }

      toast.success("Gear set removed from callsheet.");
      await fetchAllocations();
    } catch {
      toast.error("Failed to remove gear set.");
    } finally {
      setSaving(false);
    }
  };

  const openPicker = () => {
    const initial: Record<number, number[]> = {};
    (selectedSet?.items || []).forEach((item) => {
      if (item.brand_model) initial[item.brand_model] = [];
    });
    setPicks(initial);
    setPickerOpen(true);
  };

  const togglePick = (
    brandModelId: number,
    equipmentId: number,
    maxQty: number,
  ) => {
    setPicks((prev) => {
      const current = prev[brandModelId] || [];
      if (current.includes(equipmentId)) {
        return {
          ...prev,
          [brandModelId]: current.filter((id) => id !== equipmentId),
        };
      }
      if (current.length >= maxQty) return prev; // already at requirement
      return { ...prev, [brandModelId]: [...current, equipmentId] };
    });
  };

  if (loadingGearSets) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const displayItems = selectedSetId ? buildDisplayItems(selectedSetId) : [];

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Typography
        sx={{
          mb: 2,
          fontWeight: 500,
          color: colors.primary[100],
          fontSize: "1.1rem",
        }}
      >
        Select gear set for stream
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 4,
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          pb: 1,
        }}
      >
        {gearSets.map((set) => {
          const rowsForSet = getAllocationsForGearSet(set.id);
          const allocated = rowsForSet.length > 0;
          const confirmed = rowsForSet.some(
            (a) => a.allocation_type === "confirmed",
          );
          const isSelected = selectedSetId === set.id;

          return (
            <Paper
              key={set.id}
              elevation={0}
              onClick={() => setSelectedSetId(set.id)}
              sx={{
                p: 2,
                height: "150px",
                minWidth: "200px",
                width: "200px",
                flexShrink: 0,
                cursor: "pointer",
                borderRadius: "20px",
                border: `1px solid ${
                  isSelected
                    ? colors.primary[400]
                    : allocated
                      ? colors.primary[500]
                      : colors.primary[800]
                }`,
                backgroundColor: isSelected
                  ? colors.primary[400]
                  : colors.primary[900],
                color: isSelected ? colors.primary[900] : colors.primary[100],
                display: "flex",
                flexDirection: "column",
                transition: "0.2s all ease-in-out",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  mb: 0.5,
                  whiteSpace: "pre-line",
                  lineHeight: 1.2,
                }}
              >
                {set.name}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.65rem",
                  opacity: 0.8,
                  mb: 1,
                  flexGrow: 1,
                  lineHeight: 1.3,
                }}
              >
                {set.description || "No description"}
              </Typography>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.4,
                  borderRadius: "20px",
                  backgroundColor: colors.primary[800],
                  color: isSelected ? colors.primary[400] : "#64748B",
                  width: "fit-content",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              >
                <InventoryIcon sx={{ fontSize: "12px" }} />
                {set.items_count}
              </Box>

              {allocated && (
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    mt: 1,
                    fontWeight: 600,
                    color: isSelected
                      ? colors.primary[900]
                      : confirmed
                        ? "#4caf7d"
                        : colors.primary[400],
                  }}
                >
                  {confirmed ? "Confirmed" : "Planned"}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: colors.primary[100], mb: 0.5 }}
          >
            {selectedSet?.name || "No gear set selected"}
          </Typography>
          <Typography sx={{ color: TEXT_MUTED, fontSize: "1.1rem" }}>
            {selectedSet?.description || "Select a gear set to view items"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {isGearSetAlreadyAllocated && !isConfirmed && (
            <>
              <Button
                variant="contained"
                disabled={saving}
                onClick={handleConfirmAuto}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#4caf7d",
                  "&:hover": { backgroundColor: "#3d9c68" },
                }}
              >
                {saving ? "Confirming..." : "Confirm (Auto)"}
              </Button>
              <Button
                variant="outlined"
                disabled={saving}
                onClick={openPicker}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Pick Specific Units
              </Button>
            </>
          )}

          <Button
            variant="contained"
            disabled={saving || !selectedSetId}
            onClick={
              isGearSetAlreadyAllocated
                ? handleUnallocateGearSet
                : handleAllocateGearSet
            }
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {saving
              ? "Saving..."
              : isGearSetAlreadyAllocated
                ? "Remove from Callsheet"
                : "Add Set to Callsheet"}
          </Button>

          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            disabled={!selectedSet}
            onClick={() => setEditSetOpen(true)}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              color: colors.primary[100],
              borderColor: colors.primary[800],
              px: 2.5,
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "2fr 1.2fr 1fr 80px",
          p: "12px 20px",
          mb: 2,
          backgroundColor: colors.primary[800],
          borderRadius: "50px",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          Name
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          Category
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          {isConfirmed ? "Status" : "In Stock"}
        </Typography>
        <Typography
          sx={{ fontWeight: 700, fontSize: "0.95rem", textAlign: "right" }}
        >
          Qty
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.primary[800],
          borderRadius: "24px",
          p: "10px 20px 30px 20px",
        }}
      >
        {!isConfirmed &&
          (selectedSet?.items || []).map((item) => {
            const bm = getBrandModel(item.brand_model);
            const name = item.brand_model_name || bm?.model || "Unnamed gear";
            const category =
              (typeof bm?.category === "object"
                ? bm?.category?.name
                : bm?.category) || "—";
            const available = bm?.available_quantity ?? 0;
            const total = bm?.brand_model_quantity ?? 0;
            const quantity = item.quantity || 1;
            const short = available < quantity;

            return (
              <Box
                key={item.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.2fr 1fr 80px",
                  py: 1.8,
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: colors.primary[100],
                    fontWeight: 500,
                  }}
                >
                  {name}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.9rem", color: colors.primary[100] }}
                >
                  {category}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: short ? "#ffc107" : "#64748B",
                  }}
                >
                  {available}/{total} available
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    color: colors.primary[100],
                    textAlign: "right",
                    fontWeight: 500,
                    pr: 1,
                  }}
                >
                  {quantity}
                </Typography>
              </Box>
            );
          })}

        {isConfirmed &&
          displayItems.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1.2fr 1fr 80px",
                py: 1.8,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: colors.primary[100],
                  fontWeight: 500,
                }}
              >
                {item.name}
              </Typography>
              <Typography
                sx={{ fontSize: "0.9rem", color: colors.primary[100] }}
              >
                {item.category}
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#4caf7d" }}>
                Reserved
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  color: colors.primary[100],
                  textAlign: "right",
                  fontWeight: 500,
                  pr: 1,
                }}
              >
                {item.quantity}
              </Typography>
            </Box>
          ))}

        {(isConfirmed
          ? displayItems.length === 0
          : selectedSet?.items?.length === 0) && (
          <Typography sx={{ py: 3, color: TEXT_MUTED }}>
            No items found in this gear set.
          </Typography>
        )}
      </Box>

      {loadingAllocations && (
        <Typography sx={{ mt: 2, color: TEXT_MUTED }}>
          Loading current gear allocations...
        </Typography>
      )}

      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight={700}>
              Pick Specific Units — {selectedSet?.name}
            </Typography>
            <IconButton onClick={() => setPickerOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {(selectedSet?.items || []).map((item) => {
            const bmId = item.brand_model ?? -1;
            const needed = item.quantity || 1;
            const chosen = picks[bmId] || [];
            const availableUnits = Object.values(equipmentMap).filter(
              (u) =>
                u.brand_model_id === bmId &&
                (u.status === "available" || chosen.includes(u.id)),
            );

            return (
              <Box key={item.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                  {item.brand_model_name} — need {needed}, picked{" "}
                  {chosen.length}
                </Typography>

                {availableUnits.length === 0 && (
                  <Typography variant="caption" color="error">
                    No available units for this model.
                  </Typography>
                )}

                {availableUnits.map((unit) => (
                  <Box
                    key={unit.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ py: 0.3 }}
                  >
                    <input
                      type="checkbox"
                      checked={chosen.includes(unit.id)}
                      onChange={() => togglePick(bmId, unit.id, needed)}
                    />
                    <Typography variant="body2">
                      {unit.asset_tag || unit.serial_number}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          })}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button variant="outlined" onClick={() => setPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmManual}
              disabled={saving}
            >
              {saving ? "Confirming..." : "Confirm Selection"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {selectedSet && (
        <AddSetDialog
          open={editSetOpen}
          onClose={() => setEditSetOpen(false)}
          initialData={{
            id: selectedSet.id.toString(),
            name: selectedSet.name,
            description: selectedSet.description,
            items: selectedSet.items?.map((item) => ({
              id: item.id,
              brand_model: item.brand_model ?? 0,
              brand_model_name: item.brand_model_name || "",
              quantity: item.quantity || 1,
              notes: item.notes || "",
            })),
          }}
          isEdit
          onSubmit={async (data) => {
            await apiService.put(
              `/api/equipments/gearsets/${selectedSet.id}/`,
              {
                name: data.name,
                description: data.description,
                items: data.items,
              },
            );
            toast.success("Gear set updated.");
            setEditSetOpen(false);
            await fetchGearSets();
            await fetchAllocations();
          }}
          brandModelList={brandModelList}
          preSelectedIds={[]}
        />
      )}
    </Box>
  );
};

export default GearPage;
